
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'es' : 'en';
    i18n.changeLanguage(newLang);
  };

  const currentLang = i18n.language === 'en' ? 'EN' : 'ES';
  const currentLanguage = i18n.language;

  // Logo URLs based on language
  const logoUrl = i18n.language === 'en' 
    ? 'https://static.readdy.ai/image/c96c1e7a1b8af8480a4ca8f0b32ab500/576adc76bd7544677d621af600b2a525.png'
    : 'https://static.readdy.ai/image/c96c1e7a1b8af8480a4ca8f0b32ab500/576adc76bd7544677d621af600b2a525.png';

  return (
    <header className="fixed top-0 left-0 right-0 z-50 px-4 py-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between w-full">
          {/* Logo and Church Name */}
          <div className="flex items-center space-x-4 sm:space-x-6">
            <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 flex items-center justify-center rounded-full shadow-lg overflow-hidden">
              <img 
                src="https://static.readdy.ai/image/c96c1e7a1b8af8480a4ca8f0b32ab500/576adc76bd7544677d621af600b2a525.png"
                alt={currentLanguage === 'es' 
                  ? 'Logo Iglesia Evangélica Casa del Pan'
                  : 'House of Bread Evangelical Church Logo'
                }
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:block">
              <h1 className="text-xl lg:text-3xl xl:text-4xl font-bold text-white" style={{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}>
                {currentLanguage === 'es' ? 'Casa del Pan' : 'House of Bread'}
              </h1>
              <p className="text-base lg:text-lg xl:text-xl text-white/90" style={{ textShadow: '1px 1px 2px rgba(0, 0, 0, 0.5)' }}>
                {currentLanguage === 'es' ? 'Iglesia Evangélica' : 'Evangelical Church'}
              </p>
            </div>
          </div>

          {/* Right side - Navigation */}
          <div className="flex items-center space-x-6">
            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-6">
              <button
                onClick={() => scrollToSection('home')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('events')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.events')}
              </button>
              <button
                onClick={() => scrollToSection('sermons')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.sermons')}
              </button>
              <button
                onClick={() => scrollToSection('blog')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.blog')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.contact')}
              </button>
            </nav>

            {/* Language Toggle and Give Button */}
            <div className="flex items-center space-x-3">
              <button
                onClick={toggleLanguage}
                className="px-3 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {currentLang}
              </button>
              
              <button
                onClick={() => scrollToSection('donate')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.donate')}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-white rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer"
              style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
            >
              <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 p-4 bg-black/80 backdrop-blur-sm rounded-lg border border-white/20">
            <div className="flex flex-col space-y-3">
              <button
                onClick={() => scrollToSection('home')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer text-left"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.home')}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer text-left"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.about')}
              </button>
              <button
                onClick={() => scrollToSection('events')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer text-left"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.events')}
              </button>
              <button
                onClick={() => scrollToSection('sermons')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer text-left"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.sermons')}
              </button>
              <button
                onClick={() => scrollToSection('blog')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer text-left"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.blog')}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer text-left"
                style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
              >
                {t('nav.contact')}
              </button>
              <div className="flex space-x-3 pt-2">
                <button
                  onClick={toggleLanguage}
                  className="px-3 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {currentLang}
                </button>
                <button
                  onClick={() => scrollToSection('donate')}
                  className="px-4 py-2 text-white font-medium rounded-lg border border-white/20 shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap cursor-pointer"
                  style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
                >
                  {t('nav.donate')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
